import { Box } from '@mui/material';
import { GridColDef, GridCellParams, GridSortModel } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { getGifts } from 'src/shared/services/giftService';
import { Gift } from 'src/modules/gift/models/gift';
import { FundraiserTypeEnum } from 'src/modules/gift/models/fundraiserType.enum';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import dayjs from 'dayjs';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { GiftSearchFilter } from '../models/giftSearchFilter';
import { GiftStatuses } from './giftStatuses';

type GiftDataGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchFilter: GiftSearchFilter;
  startExport: boolean;
  setStartExport: (startExport: boolean) => void;
};

export const GiftDataGrid = (props: GiftDataGridProps) => {
  const { loadingCallback, searchFilter, startExport, setStartExport } = props;
  const { t } = useTranslation(['gift']);
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'deposit_id', sort: 'desc' }]);
  const [rows, setRows] = useState<Gift[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
    setTimeout(() => {
      scrollIntoView();
    }, 100);
  };

  const fetchData = useCallback(async () => {
    updateData('loading', true);
    loadingCallback(true);
    try {
      const result = await getGifts(data.page, data.pageSize, sortModel[0].field, sortModel[0].sort, searchFilter, startExport);
      if (isMounted() && !startExport) {
        const giftsResult = extendObjectWithId(result.data.results, 'deposit_id');
        updateData('totalRows', result.data.totalItems);
        setTimeout(() => {
          if (isMounted()) {
            setRows(giftsResult);
          }
        }, 100);
      }
    } finally {
      if (isMounted()) {
        setStartExport(false);
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [data.page, data.pageSize, isMounted, loadingCallback, searchFilter, setStartExport, sortModel, startExport]);

  useEffect(() => {
    fetchData();
  }, [data.page, data.pageSize, fetchData]);

  const onRowClick = (params) => {
    navigate(`${RoutesEnum.GiftsDetail}/${params.row.id}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'deposit_id',
      headerName: t('gift:tableHeaders.deposit_id'),
      flex: 0.4,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.deposit_id}</>;
      },
    },
    {
      field: 'firstName',
      headerName: t('gift:tableHeaders.donor'),
      flex: 1.1,
      renderCell: (params: GridCellParams) => {
        return <>{`${params.row.firstName} ${params.row.lastName}`}</>;
      },
    },
    {
      field: 'email_address',
      headerName: t('gift:tableHeaders.email'),
      flex: 1.3,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.email_address}</>;
      },
    },
    {
      field: 'phone',
      headerName: t('gift:tableHeaders.phone'),
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.phone}</>;
      },
    },
    {
      field: 'type',
      headerName: t('gift:tableHeaders.type'),
      flex: 0.7,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.fundraiser?.type ? t(`gift:giftType.${FundraiserTypeEnum[params.row.fundraiser.type]}`) : '-'}</>;
      },
    },
    {
      field: 'amount',
      headerName: t('gift:tableHeaders.amount'),
      headerAlign: 'right',
      align: 'right',
      flex: 0.45,
      renderCell: (params: GridCellParams) => {
        return <AmountWithCurrency amount={params.row.amount} />;
      },
    },
    {
      field: 'createdDate',
      headerName: t('gift:tableHeaders.created'),
      flex: 0.7,
      renderCell: (params: GridCellParams) => {
        return <>{dayjs(params.row.createdDate).format('YYYY-MM-DD HH:mm')}</>;
      },
    },
    {
      field: 'paymentRefNo',
      headerName: t('gift:tableHeaders.payStatus'),
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <GiftStatuses isPaid={params.row.paid} showHandled={false} isHandled={false} chipSize='small' />;
      },
    },
    {
      field: 'isHandled',
      headerName: t('gift:tableHeaders.handled'),
      headerClassName: 'lastColumnSeparator',
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <GiftStatuses isPaid={false} showPaid={false} isHandled={!!params.row.isHandled} chipSize='small' />;
      },
    },
  ];

  return (
    <Box
      sx={{
        '& .MuiDataGrid-root': { border: 'none' },
        '& .MuiChip-filled ': { borderRadius: 1 },
      }}
      ref={scrollRef}
    >
      <DataGrid<Gift>
        columns={columns}
        rows={rows}
        autoHeight
        autoRowHeight={false}
        dataModel={data}
        checkboxSelection={false}
        onRowClick={onRowClick}
        disableSelectionOnClick
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSelectionModelChange={() => ConsoleHelper.log()}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
          }
        }}
        selectionModel={[]}
      />
    </Box>
  );
};
